import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Leaf, XCircle } from "lucide-react";
import { api, getErrorMessage } from "../api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

/** Public confirmation page reached via a signed link from the admin registration email. */
export default function BuyerActionConfirm() {
  const { token } = useParams();
  const [state, setState] = useState(null); // { ok, action, buyer } or { ok: false, reason }
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/buyer-action/${token}/preview`).then((r) => setState(r.data)).catch((e) => setState({ ok: false, reason: getErrorMessage(e, "Link invalid") }));
  }, [token]);

  const execute = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/buyer-action/${token}`, { reason: reason || null });
      setDone(data);
    } catch (err) { toast.error(getErrorMessage(err, "Failed")); }
    finally { setBusy(false); }
  };

  if (!state) return <Frame><div className="text-[#5C665A]">Loading…</div></Frame>;
  if (done) {
    return (
      <Frame>
        <CheckCircle2 size={40} className="mx-auto text-[#40916C] mb-3" />
        <h2 className="db-h text-xl font-bold" data-testid="buyer-action-done">Buyer {done.buyer_status}</h2>
        <p className="text-sm text-[#5C665A] mt-1">Action recorded. You can close this window.</p>
      </Frame>
    );
  }
  if (!state.ok) {
    return (
      <Frame>
        <XCircle size={40} className="mx-auto text-[#C94A4A] mb-3" />
        <h2 className="db-h text-xl font-bold" data-testid="buyer-action-invalid">Link not usable</h2>
        <p className="text-sm text-[#5C665A] mt-1">{state.reason || "Please contact the admin."}</p>
        {state.already_used && <p className="text-xs text-[#5C665A] mt-2">This buyer was already {state.action === "approve" ? "approved" : "rejected"}.</p>}
      </Frame>
    );
  }
  const b = state.buyer;
  const isApprove = state.action === "approve";
  return (
    <Frame>
      <h2 className="db-h text-xl font-bold" data-testid="buyer-action-title">{isApprove ? "Approve buyer?" : "Reject buyer?"}</h2>
      <div className="mt-4 border border-[#E2E4E0] rounded-md p-4 text-left text-sm bg-[#F9F9F8]" data-testid="buyer-action-details">
        <div><span className="text-[#5C665A]">Name:</span> {b.first_name} {b.last_name}</div>
        <div><span className="text-[#5C665A]">Email:</span> {b.email}</div>
        <div><span className="text-[#5C665A]">Company:</span> {b.company || "—"}</div>
        <div><span className="text-[#5C665A]">VAT:</span> {b.vat_id || "—"}</div>
        <div><span className="text-[#5C665A]">Import licence:</span> {b.import_license ? (b.import_license_number || "yes") : "no"}</div>
      </div>
      {!isApprove && (
        <div className="mt-4 text-left">
          <label className="text-xs text-[#5C665A]">Reason (optional)</label>
          <Textarea data-testid="buyer-action-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
        </div>
      )}
      <div className="flex justify-end mt-6">
        <Button data-testid="buyer-action-submit" disabled={busy} onClick={execute} className={`h-10 text-white ${isApprove ? "bg-[#2D6A4F] hover:bg-[#1B4332]" : "bg-[#C94A4A] hover:bg-[#a83a3a]"}`}>
          {busy ? "Processing…" : isApprove ? "Approve" : "Reject"}
        </Button>
      </div>
    </Frame>
  );
}

function Frame({ children }) {
  return (
    <div className="min-h-screen bg-[#F9F9F8] grid place-items-center p-6">
      <div className="w-full max-w-md bg-white rounded-lg border border-[#E2E4E0] p-8 text-center">
        <div className="inline-flex items-center gap-2 db-h mb-4">
          <span className="w-8 h-8 rounded-md bg-[#2D6A4F] text-white grid place-items-center"><Leaf size={16} /></span>
          <div className="text-sm font-bold">DocBlock Wholesale</div>
        </div>
        {children}
      </div>
    </div>
  );
}
